import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #transitionRevision = 0;
  #accordionAnimationTimers = new Set();
  #scrollBlockActive = false;

  #onViewportChange = () => {
    if (this.isOpen) this.#syncDrawerTop();
  };

  #blockPageScroll = (event) => {
    if (!this.#scrollBlockActive) return;

    const target = event.target;

    /*
     * The cart root covers the whole viewport, including its backdrop.
     * Only the cart BODY is a valid scroll area.
     */
    if (
      target instanceof Element &&
      target.closest('#celora-fallback-cart .celora-fallback-cart__body')
    ) {
      return;
    }

    event.preventDefault();
  };

  #enableScrollBlock() {
    if (this.#scrollBlockActive) return;

    this.#scrollBlockActive = true;

    document.addEventListener('touchmove', this.#blockPageScroll, {
      passive: false,
      capture: true,
    });

    document.addEventListener('wheel', this.#blockPageScroll, {
      passive: false,
      capture: true,
    });
  }

  #disableScrollBlock() {
    if (!this.#scrollBlockActive) return;

    this.#scrollBlockActive = false;

    document.removeEventListener(
      'touchmove',
      this.#blockPageScroll,
      true
    );

    document.removeEventListener(
      'wheel',
      this.#blockPageScroll,
      true
    );
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'keyup',
      this.#onKeyUp
    );

    this.addEventListener(
      'animationend',
      this.#onAnimatedElementEnd
    );

    window.addEventListener(
      'resize',
      this.#onViewportChange,
      { passive: true }
    );

    window.visualViewport?.addEventListener(
      'resize',
      this.#onViewportChange,
      { passive: true }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(
      'keyup',
      this.#onKeyUp
    );

    this.removeEventListener(
      'animationend',
      this.#onAnimatedElementEnd
    );

    window.removeEventListener(
      'resize',
      this.#onViewportChange
    );

    window.visualViewport?.removeEventListener(
      'resize',
      this.#onViewportChange
    );

    this.#disableScrollBlock();

    this.#accordionAnimationTimers.forEach(
      (timer) => clearTimeout(timer)
    );

    this.#accordionAnimationTimers.clear();

    this.querySelectorAll(
      'accordion-custom .details-content--no-animation'
    ).forEach((element) => {
      element.classList.remove(
        'details-content--no-animation'
      );
    });
  }

  /**
   * Close the main menu drawer when Escape is pressed.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(
      this.#getDetailsElement(event)
    );
  };

  /**
   * Remove will-change after menu animation.
   *
   * @param {AnimationEvent} event
   */
  #onAnimatedElementEnd = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    if (
      !target.classList.contains(
        'menu-drawer__animated-element'
      )
    ) {
      return;
    }

    removeWillChangeOnAnimationEnd(event);
  };

  /**
   * Whether the main Header drawer is open.
   */
  get isOpen() {
    return this.refs.details.hasAttribute(
      'open'
    );
  }

  /**
   * Get closest details element.
   *
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (
      !(event?.target instanceof Element)
    ) {
      return this.refs.details;
    }

    return (
      event.target.closest('details') ??
      this.refs.details
    );
  }

  /**
   * Measure only the currently visible Header.
   */
  #syncDrawerTop() {
    const headerComponent =
      this.closest('#header-component') ||
      this.closest('header-component') ||
      document.querySelector(
        '#header-component'
      ) ||
      document.querySelector(
        'header-component'
      );

    let bottom = 0;

    if (
      headerComponent instanceof HTMLElement
    ) {
      const rect =
        headerComponent.getBoundingClientRect();

      if (rect.height > 0) {
        bottom = rect.bottom;
      }
    }

    /*
     * Fallback to visible Header section only.
     */
    if (bottom <= 0) {
      const headerSection =
        this.closest('.header-section');

      if (
        headerSection instanceof HTMLElement
      ) {
        const rect =
          headerSection.getBoundingClientRect();

        if (rect.height > 0) {
          bottom = rect.bottom;
        }
      }
    }

    /*
     * Final fallback using theme variables.
     */
    if (bottom <= 0) {
      const styles =
        getComputedStyle(document.body);

      const groupHeight = parseFloat(
        styles.getPropertyValue(
          '--header-group-height'
        )
      );

      const headerHeight = parseFloat(
        styles.getPropertyValue(
          '--header-height'
        )
      );

      bottom =
        Number.isFinite(groupHeight) &&
        groupHeight > 0
          ? groupHeight
          : Number.isFinite(headerHeight) &&
              headerHeight > 0
            ? headerHeight
            : 60;
    }

    const viewportHeight =
      window.visualViewport?.height ||
      window.innerHeight;

    const safeBottom = Math.max(
      0,
      Math.min(
        viewportHeight,
        bottom
      )
    );

    /*
     * Avoid 1px/sub-pixel Safari gap.
     */
    this.style.setProperty(
      '--celora-header-drawer-top',
      `${Math.ceil(safeBottom)}px`
    );
  }

  /**
   * Toggle Header drawer.
   */
  toggle() {
    return this.isOpen
      ? this.close()
      : this.open();
  }

  /**
   * Open Header drawer/submenu.
   *
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details =
      this.#getDetailsElement(event);

    const summary =
      details.querySelector('summary');

    if (!summary) return;

    this.#syncDrawerTop();

    /*
     * Only activate the page scroll blocker
     * for the MAIN Header drawer.
     */
    if (
      details === this.refs.details
    ) {
      this.#enableScrollBlock();
    }

    const revision =
      ++this.#transitionRevision;

    summary.setAttribute(
      'aria-expanded',
      'true'
    );

    this.preventInitialAccordionAnimations(
      details
    );

    requestAnimationFrame(() => {
      if (
        revision !==
        this.#transitionRevision
      ) {
        return;
      }

      if (
        !details.hasAttribute('open')
      ) {
        return;
      }

      /*
       * Recalculate once again because
       * iPhone Safari sticky geometry can update
       * between frames.
       */
      this.#syncDrawerTop();

      details.classList.add(
        'menu-open'
      );

      if (target) {
        this.refs.menuDrawer.classList.add(
          'menu-drawer--has-submenu-opened'
        );
      }

      const drawer =
        details.querySelector(
          '.menu-drawer, .menu-drawer__submenu'
        );

      onAnimationEnd(
        drawer || details,
        () => {
          if (
            revision !==
            this.#transitionRevision
          ) {
            return;
          }

          if (
            !details.hasAttribute('open') ||
            !details.classList.contains(
              'menu-open'
            )
          ) {
            return;
          }

          trapFocus(details);
        },
        {
          subtree: false,
        }
      );
    });
  }

  /**
   * Go back from submenu.
   *
   * @param {Event} [event]
   */
  back(event) {
    this.#close(
      this.#getDetailsElement(event)
    );
  }

  /**
   * Close main Header drawer.
   */
  close() {
    this.#close(
      this.refs.details
    );
  }

  /**
   * Close menu/submenu.
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary =
      details.querySelector('summary');

    if (!summary) return;

    this.#syncDrawerTop();

    const revision =
      ++this.#transitionRevision;

    summary.setAttribute(
      'aria-expanded',
      'false'
    );

    details.classList.remove(
      'menu-open'
    );

    const drawer =
      details.querySelector(
        '.menu-drawer, .menu-drawer__submenu'
      );

    onAnimationEnd(
      drawer || details,
      () => {
        if (
          revision !==
          this.#transitionRevision
        ) {
          return;
        }

        if (
          details.classList.contains(
            'menu-open'
          )
        ) {
          return;
        }

        reset(details);

        if (
          details === this.refs.details
        ) {
          removeTrapFocus();

          const openDetails =
            this.querySelectorAll(
              'details[open]:not(accordion-custom > details)'
            );

          openDetails.forEach(reset);

          /*
           * Header menu is closed:
           * restore normal page/cart behaviour.
           */
          this.#disableScrollBlock();
        } else {
          trapFocus(
            this.refs.details
          );
        }

        this.#syncSubmenuState();
      },
      {
        subtree: false,
      }
    );
  }

  /**
   * Keep submenu state synchronized.
   */
  #syncSubmenuState() {
    const hasOpenSubmenu =
      Array.from(
        this.refs.details.querySelectorAll(
          'details[open]:not(accordion-custom > details)'
        )
      ).some(
        (details) =>
          details !== this.refs.details
      );

    this.refs.menuDrawer.classList.toggle(
      'menu-drawer--has-submenu-opened',
      hasOpenSubmenu
    );
  }

  /**
   * Temporarily disable accordion animations.
   *
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(
    details
  ) {
    const content =
      details.querySelectorAll(
        'accordion-custom .details-content'
      );

    content.forEach((element) => {
      if (
        element instanceof HTMLElement
      ) {
        element.classList.add(
          'details-content--no-animation'
        );
      }
    });

    const timer = setTimeout(() => {
      this.#accordionAnimationTimers.delete(
        timer
      );

      content.forEach((element) => {
        if (
          element instanceof HTMLElement
        ) {
          element.classList.remove(
            'details-content--no-animation'
          );
        }
      });
    }, 100);

    this.#accordionAnimationTimers.add(
      timer
    );
  }
}

if (
  !customElements.get(
    'header-drawer'
  )
) {
  customElements.define(
    'header-drawer',
    HeaderDrawer
  );
}

/**
 * Reset an open details element.
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove(
    'menu-open'
  );

  element.removeAttribute(
    'open'
  );

  element
    .querySelector('summary')
    ?.setAttribute(
      'aria-expanded',
      'false'
    );
}